import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedValue?: any;
}

@Injectable({
  providedIn: 'root'
})
export class InputValidationService {
  
  // XSS patterns to detect and prevent
  private readonly xssPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
    /<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi,
    /<link\b[^>]*>/gi,
    /<meta\b[^>]*>/gi,
    /javascript:/gi,
    /vbscript:/gi,
    /data:text\/html/gi,
    /on\w+\s*=/gi, // Event handlers like onclick, onload, etc.
    /expression\s*\(/gi,
    /url\s*\(/gi,
    /@import/gi
  ];

  // SQL injection patterns
  private readonly sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi,
    /(--|\/\*|\*\/|;|'|"|\||&)/g,
    /(\bOR\b|\bAND\b)\s+\d+\s*=\s*\d+/gi,
    /\b(UNION|SELECT)\b.*\b(FROM|WHERE)\b/gi
  ];

  // Command injection patterns
  private readonly commandPatterns = [
    /[;&|`(){}[\]\\]/g, // Removed $ from this pattern as it's valid in passwords
    /\b(rm|del|format|shutdown|reboot|kill|ps|ls|dir|cat|type|net|ping|wget|curl)\b/gi
  ];

  // Password-specific command injection patterns (more restrictive)
  private readonly passwordCommandPatterns = [
    /[;&|`\\]/g, // Only check for most dangerous characters in passwords
    /\b(rm|del|format|shutdown|reboot|kill|ps|ls|dir|cat|type|net|ping|wget|curl)\b/gi
  ];

  // Path traversal patterns
  private readonly pathTraversalPatterns = [
    /\.\.[\/\\]/g,
    /[\/\\]\.\.[\/\\]/g,
    /%2e%2e[\/\\]/gi,
    /%252e%252e[\/\\]/gi
  ];

  constructor() {}

  /**
   * Comprehensive input validation and sanitization
   */
  validateAndSanitize(input: any, type: 'text' | 'email' | 'url' | 'number' | 'html' | 'filename' | 'json' | 'password' = 'text'): ValidationResult {
    if (input === null || input === undefined) {
      return { isValid: true, errors: [], sanitizedValue: input };
    }

    const errors: string[] = [];
    let sanitizedValue = input;

    // Convert to string for validation
    const stringValue = String(input);

    // Check for XSS attempts
    if (this.containsXSS(stringValue)) {
      errors.push('Input contains potentially malicious content');
    }

    // Check for SQL injection
    if (this.containsSQLInjection(stringValue)) {
      errors.push('Input contains SQL injection patterns');
    }

    // Check for command injection (use password-specific patterns for passwords)
    if (type === 'password') {
      if (this.containsPasswordCommandInjection(stringValue)) {
        errors.push('Input contains command injection patterns');
      }
    } else {
      if (this.containsCommandInjection(stringValue)) {
        errors.push('Input contains command injection patterns');
      }
    }

    // Check for path traversal
    if (this.containsPathTraversal(stringValue)) {
      errors.push('Input contains path traversal patterns');
    }

    // Type-specific validation and sanitization
    switch (type) {
      case 'email':
        const emailResult = this.validateEmail(stringValue);
        if (!emailResult.isValid) {
          errors.push(...emailResult.errors);
        }
        sanitizedValue = emailResult.sanitizedValue;
        break;

      case 'url':
        const urlResult = this.validateURL(stringValue);
        if (!urlResult.isValid) {
          errors.push(...urlResult.errors);
        }
        sanitizedValue = urlResult.sanitizedValue;
        break;

      case 'number':
        const numberResult = this.validateNumber(input);
        if (!numberResult.isValid) {
          errors.push(...numberResult.errors);
        }
        sanitizedValue = numberResult.sanitizedValue;
        break;

      case 'html':
        sanitizedValue = this.sanitizeHTML(stringValue);
        break;

      case 'filename':
        const filenameResult = this.validateFilename(stringValue);
        if (!filenameResult.isValid) {
          errors.push(...filenameResult.errors);
        }
        sanitizedValue = filenameResult.sanitizedValue;
        break;

      case 'json':
        const jsonResult = this.validateJSON(stringValue);
        if (!jsonResult.isValid) {
          errors.push(...jsonResult.errors);
        }
        sanitizedValue = jsonResult.sanitizedValue;
        break;

      default:
        sanitizedValue = this.sanitizeText(stringValue);
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue
    };
  }

  /**
   * Angular form validators
   */
  
  // XSS validator
  xssValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const result = this.validateAndSanitize(control.value, 'text');
      return result.isValid ? null : { xss: { message: result.errors.join(', ') } };
    };
  }

  // SQL injection validator
  sqlInjectionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      if (this.containsSQLInjection(String(control.value))) {
        return { sqlInjection: { message: 'Input contains SQL injection patterns' } };
      }
      return null;
    };
  }

  // Secure email validator
  secureEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const result = this.validateEmail(String(control.value));
      return result.isValid ? null : { email: { message: result.errors.join(', ') } };
    };
  }

  // Strong password validator
  strongPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      const password = String(control.value);
      const errors: string[] = [];

      if (password.length < 12) {
        errors.push('Password must be at least 12 characters long');
      }

      if (!/[A-Z]/.test(password)) {
        errors.push('Password must contain at least one uppercase letter');
      }

      if (!/[a-z]/.test(password)) {
        errors.push('Password must contain at least one lowercase letter');
      }

      if (!/\d/.test(password)) {
        errors.push('Password must contain at least one number');
      }

      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        errors.push('Password must contain at least one special character');
      }

      // Check for common patterns
      if (/(.)\1{2,}/.test(password)) {
        errors.push('Password cannot contain repeated characters');
      }

      if (/123|abc|qwe|password|admin/i.test(password)) {
        errors.push('Password cannot contain common patterns');
      }

      return errors.length > 0 ? { strongPassword: { message: errors.join(', ') } } : null;
    };
  }

  /**
   * Private validation methods
   */

  private containsXSS(input: string): boolean {
    return this.xssPatterns.some(pattern => pattern.test(input));
  }

  private containsSQLInjection(input: string): boolean {
    return this.sqlPatterns.some(pattern => pattern.test(input));
  }

  private containsCommandInjection(input: string): boolean {
    return this.commandPatterns.some(pattern => pattern.test(input));
  }

  private containsPasswordCommandInjection(input: string): boolean {
    return this.passwordCommandPatterns.some(pattern => pattern.test(input));
  }

  private containsPathTraversal(input: string): boolean {
    return this.pathTraversalPatterns.some(pattern => pattern.test(input));
  }

  private validateEmail(email: string): ValidationResult {
    const errors: string[] = [];
    
    // Basic email format validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      errors.push('Invalid email format');
    }

    // Check for dangerous characters
    if (/[<>'"&]/.test(email)) {
      errors.push('Email contains invalid characters');
    }

    // Length validation
    if (email.length > 254) {
      errors.push('Email is too long');
    }

    const sanitizedValue = email.toLowerCase().trim();

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue
    };
  }

  private validateURL(url: string): ValidationResult {
    const errors: string[] = [];
    let sanitizedValue = url.trim();

    try {
      const urlObj = new URL(url);
      
      // Only allow HTTP and HTTPS protocols
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        errors.push('Only HTTP and HTTPS URLs are allowed');
      }

      // Check for suspicious patterns
      if (urlObj.hostname.includes('localhost') || 
          urlObj.hostname.includes('127.0.0.1') ||
          urlObj.hostname.includes('0.0.0.0')) {
        errors.push('Local URLs are not allowed');
      }

    } catch (e) {
      errors.push('Invalid URL format');
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue
    };
  }

  private validateNumber(input: any): ValidationResult {
    const errors: string[] = [];
    let sanitizedValue = input;

    if (isNaN(Number(input))) {
      errors.push('Invalid number format');
    } else {
      sanitizedValue = Number(input);
      
      // Check for reasonable bounds
      if (sanitizedValue > Number.MAX_SAFE_INTEGER || sanitizedValue < Number.MIN_SAFE_INTEGER) {
        errors.push('Number is out of safe range');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue
    };
  }

  private validateFilename(filename: string): ValidationResult {
    const errors: string[] = [];
    
    // Check for invalid characters
    const invalidChars = /[<>:"/\\|?*\x00-\x1f]/;
    if (invalidChars.test(filename)) {
      errors.push('Filename contains invalid characters');
    }

    // Check for reserved names (Windows)
    const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i;
    if (reservedNames.test(filename)) {
      errors.push('Filename uses reserved name');
    }

    // Length validation
    if (filename.length > 255) {
      errors.push('Filename is too long');
    }

    const sanitizedValue = filename.trim();

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue
    };
  }

  private validateJSON(jsonString: string): ValidationResult {
    const errors: string[] = [];
    let sanitizedValue = jsonString;

    try {
      const parsed = JSON.parse(jsonString);
      sanitizedValue = JSON.stringify(parsed); // Re-stringify to normalize
    } catch (e) {
      errors.push('Invalid JSON format');
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue
    };
  }

  private sanitizeText(text: string): string {
    return text
      .replace(/[<>]/g, '') // Remove angle brackets
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/vbscript:/gi, '') // Remove vbscript: protocol
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .trim();
  }

  private sanitizeHTML(html: string): string {
    // Basic HTML sanitization - remove dangerous tags and attributes
    return html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
      .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
      .replace(/<link\b[^>]*>/gi, '')
      .replace(/<meta\b[^>]*>/gi, '')
      .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '') // Remove event handlers
      .replace(/javascript:/gi, '')
      .replace(/vbscript:/gi, '')
      .trim();
  }

  /**
   * Utility methods for common validation scenarios
   */

  // Validate admin user input
  validateAdminInput(input: any): ValidationResult {
    return this.validateAndSanitize(input, 'text');
  }

  // Validate file upload
  validateFileUpload(file: File, allowedTypes: string[] = [], maxSize: number = 10 * 1024 * 1024): ValidationResult {
    const errors: string[] = [];

    // Check file type
    if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
      errors.push(`File type ${file.type} is not allowed`);
    }

    // Check file size
    if (file.size > maxSize) {
      errors.push(`File size exceeds maximum allowed size of ${maxSize} bytes`);
    }

    // Validate filename
    const filenameResult = this.validateFilename(file.name);
    if (!filenameResult.isValid) {
      errors.push(...filenameResult.errors);
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: file
    };
  }

  // Batch validation for form data
  validateFormData(formData: { [key: string]: any }, validationRules: { [key: string]: string }): { [key: string]: ValidationResult } {
    const results: { [key: string]: ValidationResult } = {};

    for (const [field, value] of Object.entries(formData)) {
      const validationType = validationRules[field] || 'text';
      results[field] = this.validateAndSanitize(value, validationType as any);
    }

    return results;
  }
}